import React from 'react'
import { usePage } from '@inertiajs/react'
import OnboardingStepper from '@/components/onboarding/OnboardingStepper'
import { FALLBACK_BRAND, LEGAL_NAME } from '@/lib/brand'
import { Wordmark } from '@/components/Wordmark'
import '@/pages/marketing/marketing3.css'

// The claim funnel's bare-header chrome (claim-12). Modelled on onboarding/Index.tsx's
// header/footer rather than OperatorLayout: the entry step (SetPassword) is reached
// UNAUTHENTICATED from the invitation email, so there is no Sidebar/TopBar/property switcher
// pre-auth. Extracted into one shell so the claim pages (SetPassword / Review / PaymentCapture
// / PaymentStatus) cannot drift apart, and so the three-step indicator is identical across them.
//
// Dressed in the landing3 (mkt3-*) design language so the claim funnel reads as a sibling of
// the public landing the customer just came through — warm-stone canvas, faint teal wash, the
// three-bar ledger glyph + {brand} wordmark — rather than wearing generic operator chrome.
// The whole tree is wrapped in `.mkt3-root` so the marketing3.css variables + component classes
// resolve; the CSS is imported here so the bundle is present on every claim page.
//
// The three funnel steps mirror the controller flow (FE3 reshaped the checkout tail into
// in-app card capture: the claimant saves a card in-app, then a status page polls activation):
//   1. Set password  (claim/SetPassword)
//   2. Review        (claim/Review)
//   3. Payment       (claim/PaymentCapture → claim/PaymentStatus)
export const CLAIM_STEPS = [
  { label: 'Set password', description: 'Secure your account' },
  { label: 'Review', description: 'Confirm your plan' },
  { label: 'Payment', description: 'Add your card' },
]

/** The ledger wordmark glyph — three stacked bars (copied from Marketing3Layout). */
function LedgerGlyph() {
  return (
    <span aria-hidden="true" className="flex flex-col gap-[3px]">
      <span className="h-[3px] w-5 rounded-full bg-teal-600" />
      <span className="h-[3px] w-3.5 rounded-full bg-slate-400" />
      <span className="h-[3px] w-2 rounded-full bg-stone-300" />
    </span>
  )
}

interface ClaimShellProps {
  /** 1-based current step, drives the stepper highlight. */
  currentStep: number
  /** Optional footer caption (e.g. the signed-in email once the claimant authenticates). */
  footerNote?: string
  children: React.ReactNode
}

export default function ClaimShell({ currentStep, footerNote, children }: ClaimShellProps) {
  const { props } = usePage()
  const brand =
    typeof props.brand === 'string' && props.brand.trim() !== '' ? props.brand : FALLBACK_BRAND

  return (
    <div className="mkt3-root relative min-h-screen pb-14">
      {/* Soft teal wash from the top — the landing's "pool of light", quieted for a funnel page. */}
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-72 mkt3-light-glow" />

      {/* Bare header — brand mark only, no nav. */}
      <header className="relative border-b border-stone-200/70 bg-[#fafaf9]/80 backdrop-blur-md">
        <div className="mx-auto max-w-3xl px-6 py-4">
          <div className="flex items-center gap-3">
            <LedgerGlyph />
            <div className="flex items-baseline gap-2.5">
              <Wordmark brand={brand} suffix="RMS" className="mkt3-display text-[1.6rem]" />
              <span className="mkt3-label normal-case tracking-[0.16em] text-slate-500">
                Claim your account
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="relative mx-auto max-w-3xl px-6 py-10">
        <div className="mkt3-rise">
          <OnboardingStepper currentStep={currentStep} steps={CLAIM_STEPS} />
        </div>

        <div className="mt-10">{children}</div>
      </main>

      <footer className="fixed bottom-0 left-0 right-0 border-t border-stone-200/70 bg-[#fafaf9]/80 backdrop-blur-md">
        <div className="mx-auto max-w-3xl px-6 py-3">
          <p className="text-center text-xs text-slate-500">
            {footerNote ?? `© 2026 ${LEGAL_NAME}. Built and run by hoteliers.`}
          </p>
        </div>
      </footer>
    </div>
  )
}
